import { useId } from "react";
import type { SearchCompanyRow } from "@/lib/staff-api";
import { apiParams, readDir } from "@/lib/list-url";
import { staffCan } from "@/lib/staff-capabilities";
import { useStaffSearch } from "@/lib/staff-hooks";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { CompaniesTable } from "./CompaniesTable";
import { EmployeesTable } from "./EmployeesTable";
import { Loaded } from "./Loaded";
import { UrlTickbox } from "./ListControls";
import { Pager } from "./Pager";
import { useListUrl, usePageCorrection, useSortHandler } from "./use-list-url";

const MATCHED: Record<SearchCompanyRow["matched"], string> = {
  company: "Matched company name",
  contact: "Matched contact name",
  email: "Matched email",
};

const COMPANIES = { sort: "sort_companies", dir: "dir_companies", page: "page_companies" };
const EMPLOYEES = { sort: "sort_employees", dir: "dir_employees", page: "page_employees" };

/** The header search's results: matching companies, then matching employees,
 *  each sorted and paged on its own parameters. */
export function StaffSearchPage(): React.ReactElement {
  const companiesId = useId();
  const employeesId = useId();
  const { staff } = useStaffAuth();
  const isIt = staff !== null && staffCan(staff.role, "manage_staff");
  const canAct = staff !== null && staffCan(staff.role, "work_queues");
  const [url, change] = useListUrl();
  const q = (url.get("q") ?? "").trim();
  const query = useStaffSearch(apiParams(url, isIt));
  usePageCorrection(COMPANIES.page, query.isPlaceholderData ? undefined : query.data?.companies.page);
  usePageCorrection(EMPLOYEES.page, query.isPlaceholderData ? undefined : query.data?.employees.page);
  const onSortCompanies = useSortHandler(COMPANIES);
  const onSortEmployees = useSortHandler(EMPLOYEES);
  const onPage = (name: string) => (p: number) => change({ [name]: p === 1 ? null : String(p) });

  if (q.length < 2) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Search</h1>
        <p className="text-sm text-gray-600">Type at least two characters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Search results for “{q}”</h1>
        {isIt && <UrlTickbox label="Show hidden companies" param="includeHidden" resets={[COMPANIES.page, EMPLOYEES.page]} />}
      </div>
      <Loaded queries={[query]}>
        {() => {
          const { companies, employees } = query.data!;
          return (
            <>
              <section aria-labelledby={companiesId} className="space-y-3">
                <h2 id={companiesId} className="text-lg font-semibold text-gray-900">
                  Companies <span className="font-normal text-gray-500">({companies.total})</span>
                </h2>
                {companies.total === 0 ? (
                  <p className="text-sm text-gray-600">No matching companies</p>
                ) : (
                  <CompaniesTable
                    caption="Matching companies"
                    rows={companies.rows}
                    sort={url.get(COMPANIES.sort) ?? "name"}
                    dir={readDir(url, COMPANIES.dir)}
                    onSort={onSortCompanies}
                    note={(r) => MATCHED[r.matched]}
                  />
                )}
                <Pager page={companies.page} pageSize={companies.pageSize} total={companies.total} onPage={onPage(COMPANIES.page)} label="Companies" />
              </section>
              <section aria-labelledby={employeesId} className="space-y-3">
                <h2 id={employeesId} className="text-lg font-semibold text-gray-900">
                  Employees <span className="font-normal text-gray-500">({employees.total})</span>
                </h2>
                {employees.total === 0 ? (
                  <p className="text-sm text-gray-600">No matching employees</p>
                ) : (
                  <EmployeesTable
                    caption="Matching employees"
                    rows={employees.rows}
                    sort={url.get(EMPLOYEES.sort) ?? "name"}
                    dir={readDir(url, EMPLOYEES.dir)}
                    onSort={onSortEmployees}
                    canAct={canAct}
                  />
                )}
                <Pager page={employees.page} pageSize={employees.pageSize} total={employees.total} onPage={onPage(EMPLOYEES.page)} label="Employees" />
              </section>
            </>
          );
        }}
      </Loaded>
    </div>
  );
}
